/**
 * Two perspectives on one verified game. The verdict comes ONLY from the fan's own stored call
 * (`call.result`, `call.pChoiceAtLock`, `call.lockedAt`); with no call there is no "you called it".
 * The final score is always `asOf` — identical for every fan.
 */
import { directorClock, team, type CallLock, type CallOffer, type DirectorLanguage, type DirectorResponse, type GameEvent, type Resolution } from '@clutch/core';
import { isChineseLanguage } from './i18n';

export type VerdictTone = 'called' | 'hurt' | 'pending' | 'void' | 'won' | 'lost' | 'neutral';
export type Verdict = {
  tone: VerdictTone;
  headline: string;
  sub?: string;
  /** "Called at Q4 3:25 · Hawks 35%" — present only with a stored call. */
  stamp?: { clock: string; pct: number; team: string; code: string; model: string };
  /** A wrong call earns a rematch prompt; a right one, a story. */
  cta: 'story' | 'rematch';
};

const pctOf = (p: number) => Math.round(p * 100);
const teamName = (code: string) => team(code).name;

/** Largest lead `side` held in these plays (verified ledger only; 0 if never ahead). */
export function maxLead(plays: readonly Pick<GameEvent, 'homeScore' | 'awayScore' | 'seq'>[], side: 'home' | 'away', uptoSeq = Infinity): number {
  let best = 0;
  for (const e of plays) {
    if (e.seq > uptoSeq) continue;
    const d = side === 'home' ? e.homeScore - e.awayScore : e.awayScore - e.homeScore;
    if (d > best) best = d;
  }
  return best;
}

export type PerspectiveInput = {
  director: Pick<DirectorResponse, 'call' | 'asOf' | 'game' | 'provenance'>;
  language: DirectorLanguage;
  /** Teams the fan follows (for the no-call verdict). */
  followed?: readonly string[];
  /** Verified plays for the "led by N" line; omitted → the line is omitted. */
  plays?: readonly Pick<GameEvent, 'homeScore' | 'awayScore' | 'seq'>[];
  /** Third person for a public story ("Maya saw it coming."); omitted → "You". */
  who?: string;
};

/** Run/comeback-style claims need a reliable history (the director flags unreliable captures). */
export function historyReliable(d: Pick<DirectorResponse, 'provenance' | 'game'>): boolean {
  if (d.game.source === 'espn_public') return false;
  return !(d.provenance?.warnings ?? []).some((w) => /unreliable|inconsistent|reconcil|skipped|ambiguous/i.test(w));
}

export function verdictFor(input: PerspectiveInput): Verdict {
  if (isChineseLanguage(input.language)) return verdictZh(input);
  return verdictLocal(input);
}

const ZH_NAME: Record<string, string> = { ATL: '老鹰', BOS: '凯尔特人', BKN: '篮网', CHA: '黄蜂', CHI: '公牛', CLE: '骑士', DAL: '独行侠', DEN: '掘金', DET: '活塞', GSW: '勇士', HOU: '火箭', IND: '步行者', LAC: '快船', LAL: '湖人', MEM: '灰熊', MIA: '热火', MIL: '雄鹿', MIN: '森林狼', NOP: '鹈鹕', NYK: '尼克斯', OKC: '雷霆', ORL: '魔术', PHI: '76人', PHX: '太阳', POR: '开拓者', SAC: '国王', SAS: '马刺', TOR: '猛龙', UTA: '爵士', WAS: '奇才' };

/** Simplified Chinese verdicts: same facts and tones as English, from the same stored call. */
function verdictZh(input: PerspectiveInput): Verdict {
  const base = verdictLocal({ ...input, language: 'en' });
  const { director: { call, asOf, game }, who } = input;
  const zt = (code: string) => ZH_NAME[code] ?? teamName(code);
  const subj = who ?? '你';
  const winner = asOf.homeScore === asOf.awayScore ? undefined : asOf.homeScore > asOf.awayScore ? game.home.tricode : game.away.tricode;
  const stamp = base.stamp ? { ...base.stamp, team: zt(base.stamp.code), ...(call ? { clock: directorClock(call.lockedAt.period, call.lockedAt.clockSec, 'zh-Hans') } : {}) } : undefined;
  switch (base.tone) {
    case 'called':
      return { ...base, ...(stamp ? { stamp } : {}), headline: `${subj}早就看到了。`, sub: call ? `锁定时模型给${zt(call.chosenTeam)}的胜率是${pctOf(call.pChoiceAtLock)}%。` : undefined } as Verdict;
    case 'hurt': {
      const m = base.sub?.match(/led by (\d+)/);
      const sub = m && call ? `${zt(call.chosenTeam)}曾领先${m[1]}分……` : winner ? `${zt(winner)}以${Math.abs(asOf.homeScore - asOf.awayScore)}分取胜。` : undefined;
      return { ...base, ...(stamp ? { stamp } : {}), headline: who ? `这一场让${who}心痛。` : '这一场，真痛。', ...(sub ? { sub } : {}) };
    }
    case 'void':
      return { ...base, ...(stamp ? { stamp } : {}), headline: '这次预测没有结果。', sub: '预测已作废，没有人输。' };
    case 'pending':
      return { ...base, ...(stamp ? { stamp } : {}), headline: `${subj}的预测仍然有效。`, sub: call ? `${zt(call.chosenTeam)}，锁定于${stamp?.clock ?? ''}。` : undefined } as Verdict;
    case 'won':
      return { ...base, headline: winner ? `你的${zt(winner)}拿下了比赛。` : base.headline };
    case 'lost':
      return { ...base, headline: '今晚不属于你的球队。', sub: '下次，做出你的预测。' };
    default:
      return { ...base, headline: asOf.status === 'final' && winner ? `${zt(winner)}击败了${zt(winner === game.home.tricode ? game.away.tricode : game.home.tricode)}。` : '目前为止的故事。' };
  }
}

function verdictLocal({ director, language: l, followed = [], plays, who }: PerspectiveInput): Verdict {
  const { call, asOf, game } = director;
  const hi = l === 'hi';
  const final = asOf.status === 'final';
  const winner = asOf.homeScore === asOf.awayScore ? undefined : asOf.homeScore > asOf.awayScore ? game.home.tricode : game.away.tricode;
  const loser = winner === game.home.tricode ? game.away.tricode : winner ? game.home.tricode : undefined;
  if (call) {
    const chosen = teamName(call.chosenTeam);
    const stamp = { clock: directorClock(call.lockedAt.period, call.lockedAt.clockSec, l), pct: pctOf(call.pChoiceAtLock), team: chosen, code: call.chosenTeam, model: call.modelVersion };
    if (call.result === 'correct') {
      const p = stamp.pct;
      const sub = p < 50
        ? hi ? `लॉक के समय मॉडल ने ${chosen} को सिर्फ़ ${p}% दिया था।` : `The model gave the ${chosen} ${p}% at the lock.`
        : hi ? `${chosen} को ${p}% पर चुना — और वे टिके रहे।` : `Backed the ${chosen} at ${p}%. They held on.`;
      const headline = who ? (hi ? `${who} ने पहले ही भांप लिया था।` : `${who} saw it coming.`) : hi ? 'आपने पहले ही भांप लिया था।' : 'You saw it coming.';
      return { tone: 'called', headline, sub, stamp, cta: 'story' };
    }
    if (call.result === 'incorrect') {
      const side = call.chosenTeam === game.home.tricode ? 'home' : 'away';
      const lead = plays && historyReliable(director) ? maxLead(plays, side, asOf.seq) : 0;
      const sub = lead >= 6
        ? hi ? `${chosen} ${lead} अंकों से आगे थे…` : `${chosen} led by ${lead}…`
        : winner ? (hi ? `${teamName(winner)} ने इसे ${Math.abs(asOf.homeScore - asOf.awayScore)} से जीता।` : `${teamName(winner)} took it by ${Math.abs(asOf.homeScore - asOf.awayScore)}.`) : undefined;
      return { tone: 'hurt', headline: who ? (hi ? `${who} के लिए यह चुभने वाला था।` : `That one hurt ${who}.`) : hi ? 'यह वाला चुभता है।' : 'That one hurts.', ...(sub ? { sub } : {}), stamp, cta: 'rematch' };
    }
    if (call.result === 'void') return { tone: 'void', headline: hi ? 'इस कॉल का कोई नतीजा नहीं।' : 'No result on this call.', sub: hi ? 'कॉल रद्द हुई। किसी की हार नहीं।' : 'It was voided. Nobody loses it.', stamp, cta: 'story' };
    return { tone: 'pending', headline: who ? (hi ? `${who} की कॉल अभी ज़िंदा है।` : `${who}’s call is still alive.`) : hi ? 'आपकी कॉल अभी ज़िंदा है।' : 'Your call is still alive.', sub: hi ? `${chosen}, ${stamp.clock} पर लॉक।` : `${chosen}, locked at ${stamp.clock}.`, stamp, cta: 'story' };
  }
  const mine = who ? undefined : followed.find((t) => t === game.home.tricode || t === game.away.tricode);
  if (final && mine && winner) {
    return mine === winner
      ? { tone: 'won', headline: hi ? `${teamName(mine)} ने कर दिखाया।` : `Your ${teamName(mine)} got it done.`, cta: 'story' }
      : { tone: 'lost', headline: hi ? `${teamName(mine)} की रात नहीं थी।` : `Not the ${teamName(mine)}’ night.`, sub: hi ? 'अगली बार, अपनी कॉल लगाइए।' : 'Next time, make your call.', cta: 'rematch' };
  }
  if (final && winner && loser) return { tone: 'neutral', headline: hi ? `${teamName(winner)} ने ${teamName(loser)} को हराया।` : `${teamName(winner)} beat the ${teamName(loser)}.`, cta: 'story' };
  return { tone: 'neutral', headline: hi ? 'अब तक की कहानी।' : 'The story so far.', cta: 'story' };
}

export type FriendVersion = { name: string; team: string; correct: boolean; headline: string; pct?: number };

/**
 * "Your friend's version": another fan's actual call in this room. Prefers a fan who made the
 * opposite call. Built from the room's locks/resolution only — never an invented friend.
 */
export function friendVersion(o: {
  resolution?: Pick<Resolution, 'results' | 'outcome'>;
  locks: readonly Pick<CallLock, 'fanId' | 'displayName' | 'choice' | 'pChoiceAtLock' | 'status'>[];
  call?: Pick<CallOffer, 'yesLabel' | 'noLabel'>;
  myFanId: string;
  myChoice?: 'yes' | 'no';
  language: DirectorLanguage;
}): FriendVersion | undefined {
  const res = o.resolution;
  if (!res || res.outcome === 'void' || !o.call) return undefined;
  const others = res.results.filter((r) => r.fanId !== o.myFanId);
  const pick = others.find((r) => o.myChoice && r.choice !== o.myChoice) ?? others[0];
  if (!pick) return undefined;
  const lock = o.locks.find((l) => l.fanId === pick.fanId && l.status === 'confirmed');
  const hi = o.language === 'hi';
  const label = pick.choice === 'yes' ? o.call.yesLabel : o.call.noLabel;
  return {
    name: pick.displayName.split(/\s+/)[0] ?? pick.displayName,
    team: label,
    correct: pick.correct,
    headline: isChineseLanguage(o.language) ? pick.correct ? '早就看到了。' : '这一场，真痛。' : pick.correct ? (hi ? 'पहले ही भांप लिया था।' : 'Saw it coming.') : hi ? 'यह वाला चुभता है।' : 'That one hurts.',
    ...(lock && Number.isFinite(lock.pChoiceAtLock) ? { pct: pctOf(lock.pChoiceAtLock) } : {}),
  };
}
